/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import {
	manualList,
	manualWebPath,
	pickManualLanguage,
	ManualLanguageType,
	ManualProductType,
} from '@src/configs/manual.config'
import { Analytics } from '@utils'
import { ManualIcon } from './manual/ManualIcons'

import kedem6Img from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Img from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import pls8Img from '@public/images/monolith/koinos8-top-angle-screen-m54.png'

/** 코드 → 썸네일 (Cart 와 같은 컷). 제품이 늘면 여기에 한 줄. */
const imageObj: { [code: string]: string } = {
	kdm6: kedem6Img,
	kdm4: kedem4Img,
	pls8: pls8Img,
}

/**
 * PDF 를 저장한다. S3 가 cross-origin 이라 `<a download>` 는 브라우저가 무시하므로
 * blob 으로 받아 내려준다(버킷 CORS 필요). CORS 가 막혀 있으면 새 탭으로 연다 — Open 과 같아진다.
 */
export const downloadPdf = async (aLang: ManualLanguageType) => {
	try {
		const res = await fetch(aLang.url)
		if (!res.ok) throw new Error(String(res.status))
		const blob = await res.blob()
		const objectUrl = URL.createObjectURL(blob)
		const aLink = document.createElement('a')
		aLink.href = objectUrl
		aLink.download = aLang.fileName
		document.body.appendChild(aLink)
		aLink.click()
		document.body.removeChild(aLink)
		setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
	} catch {
		window.open(aLang.url, '_blank', 'noopener')
	}
}

/**
 * 언어별 PDF 헤어라인 행 — 왼쪽 언어, 오른쪽 Open · Download. Kedem 은 행에 바로,
 * Koinos PLS 8 은 PDF 를 펼쳤을 때 그 아래에 놓인다(2026-10-28 사용자 결정).
 */
const PdfLanguageList = (props: { id?: string; aManual: ManualProductType }) => {
	const { aManual } = props
	return (
		<View className="mnLangs" id={props.id}>
			{aManual.languages.map(aLang => (
				<View key={aLang.code} className="mnLang">
					<span className="mnLangLabel">
						{aLang.label}
						{aLang.native !== aLang.label && (
							<span className="mnLangNative">{aLang.native}</span>
						)}
					</span>
					<View className="mnLangActions">
						<a
							className="mnAction"
							href={aLang.url}
							target="_blank"
							rel="noopener noreferrer"
							onClick={() =>
								Analytics.download('download_manual', {
									manual_code: aManual.code,
									manual_language: aLang.code,
									method: 'open',
								})
							}
						>
							Open
						</a>
						<button
							type="button"
							className="mnAction"
							onClick={() => {
								Analytics.download('download_manual', {
									manual_code: aManual.code,
									manual_language: aLang.code,
									method: 'download',
								})
								void downloadPdf(aLang)
							}}
						>
							Download
						</button>
					</View>
				</View>
			))}
		</View>
	)
}

/**
 * Manuals — Monolith(2안). `/manuals` 한 장.
 *
 * 제품 하나 = 헤어라인 행 하나. 왼쪽 썸네일(Cart 와 같은 컷) + 이름, 오른쪽:
 *  - 웹판이 있는 제품(Koinos PLS 8) = 형식 두 줄. Web(`Read online →`, `/manuals/{code}/{lang}` 으로 이동) ·
 *    PDF(펼치면 그 아래에 언어별 행 — 예전 목록 그대로)
 *  - 없는 제품(Kedem 6 · 4) = 언어별 행이 바로. 예전 화면 그대로
 * 2026-10-28 사용자 결정 — MANUAL_WEB_PLAN §0 D2.
 */
const MonolithManualsPage = () => {
	const navigate = useNavigate()
	const [openCode, setOpenCode] = React.useState<string | null>(null)

	const openWeb = (event: React.MouseEvent, aManual: ManualProductType) => {
		if (event.metaKey || event.ctrlKey || event.shiftKey) return
		event.preventDefault()
		const lang = pickManualLanguage(aManual)
		Analytics.download('download_manual', {
			manual_code: aManual.code,
			manual_language: lang,
			method: 'web',
		})
		navigate(manualWebPath(aManual.code, lang))
	}

	return (
		<View css={style}>
			<Seo />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Manuals</h1>
					<p className="mnPageLede">
						User manuals for every product. PDF, in up to ten languages. Koinos PLS 8 also
						reads on the web.
					</p>
				</View>

				<View className="mnList">
					{manualList.map(aManual => {
						const image = imageObj[aManual.code]
						const isPublished = aManual.status === 'published'
						const isOpen = openCode === aManual.code
						const listId = `mnPdf-${aManual.code}`
						return (
							<View key={aManual.code} className="mnRow">
								<View className="mnRowHead">
									{image !== undefined && (
										<View className="mnThumb">
											<img src={image} alt="" className="mnThumbImage" />
										</View>
									)}
									<h2 className="mnRowTitle">
										{aManual.code === 'pls8' ? (
											<KoinosWordmark as="span" />
										) : aManual.code.startsWith('kdm') ? (
											<KedemWordmark as="span" model={aManual.code.replace('kdm', '')} />
										) : (
											aManual.name
										)}
									</h2>
								</View>

								<View className="mnRowBody">
									{isPublished && !aManual.web && <PdfLanguageList aManual={aManual} />}
									{isPublished && aManual.web && (
										<View className="mnFormats">
											<a
												className="mnFormat"
												href={manualWebPath(aManual.code, 'en')}
												onClick={event => openWeb(event, aManual)}
											>
												<span className="mnFormatLabel">Web</span>
												<span className="mnFormatMeta">
													Read online
													<ManualIcon name="arrowRight" className="mnFormatIcon" />
												</span>
											</a>
											<button
												type="button"
												className={`mnFormat ${isOpen ? 'open' : ''}`}
												aria-expanded={isOpen}
												aria-controls={listId}
												onClick={() => setOpenCode(isOpen ? null : aManual.code)}
											>
												<span className="mnFormatLabel">PDF</span>
												<span className="mnFormatMeta">
													<ManualIcon
														name="chevronDown"
														className="mnFormatIcon mnChevron"
													/>
												</span>
											</button>
											{isOpen && <PdfLanguageList id={listId} aManual={aManual} />}
										</View>
									)}
									{!isPublished && <span className="mnRowMeta">Not yet published</span>}
								</View>
							</View>
						)
					})}
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithManualsPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnList': {
		borderTop: `1px solid ${mono.line}`,
		marginBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	// 제품 행 — 4:8. 왼쪽 썸네일+이름, 오른쪽 형식(Web · PDF).
	'.mnRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(40px, 4.2vw, 60px) 0',
		borderBottom: `1px solid ${mono.line}`,

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 28,
		},
	},

	'.mnRowHead': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 24,

		'.mnThumb': {
			width: 220,
			maxWidth: '100%',
			aspectRatio: '3 / 2',
			border: `1px solid ${mono.line}`,
			borderRadius: 6,
			backgroundColor: mono.surface,
			overflow: 'hidden',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',

			'.mnThumbImage': {
				width: '100%',
				height: '100%',
				objectFit: 'cover',
				display: 'block',
			},
		},

		'.mnRowTitle': {
			fontSize: 'clamp(28px, 2.8vw, 40px)',
			lineHeight: 1.1,
			fontWeight: 300,
			color: mono.ink,
		},
	},

	'.mnRowBody': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'center',
		minWidth: 0,

		'.mnRowMeta': {
			fontSize: 13,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
		},
	},

	// 형식 행 — 헤어라인 두 줄. 왼쪽 형식 이름, 오른쪽 언어 수 + 화살표/셰브론
	'.mnFormats': {
		width: '100%',
		maxWidth: 620,
		borderTop: `1px solid ${mono.line}`,

		'.mnFormat': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: 24,
			width: '100%',
			padding: '20px 0',
			background: 'transparent',
			border: 0,
			borderBottom: `1px solid ${mono.line}`,
			fontFamily: 'inherit',
			color: mono.ink,
			textAlign: 'left',
			cursor: 'pointer',

			'.mnFormatLabel': {
				fontSize: 20,
				fontWeight: 300,
			},
			'.mnFormatMeta': {
				display: 'flex',
				alignItems: 'center',
				gap: 12,
				fontSize: 13,
				fontWeight: 500,
				letterSpacing: '.02em',
				color: mono.faint,
				transition: 'color 200ms ease-out',
			},
			'.mnChevron': {
				transition: 'transform 200ms ease-out',
			},

			'&:hover .mnFormatMeta, &.open .mnFormatMeta': {
				color: mono.ink,
			},
			'&.open .mnChevron': {
				transform: 'rotate(180deg)',
			},
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 3,
			},
			'@media (prefers-reduced-motion: reduce)': {
				'.mnFormatMeta, .mnChevron': { transition: 'none' },
			},
		},
	},

	// 언어별 헤어라인 행 — 왼쪽 언어, 오른쪽 Open · Download
	'.mnLangs': {
		width: '100%',
		maxWidth: 620,
		borderTop: `1px solid ${mono.line}`,

		'.mnLang': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: 24,
			padding: '16px 0',
			borderBottom: `1px solid ${mono.line}`,

			'.mnLangLabel': {
				display: 'flex',
				alignItems: 'baseline',
				gap: 14,
				fontSize: 16,
				fontWeight: 400,
				color: mono.ink,
			},
			'.mnLangNative': {
				fontSize: 14,
				fontWeight: 300,
				color: mono.faint,
			},

			'.mnLangActions': {
				display: 'flex',
				alignItems: 'center',
				gap: 28,
				flexShrink: 0,
			},
		},
	},
	// PDF 를 펼친 목록은 형식 행 안쪽에 놓인다 — 위 선은 PDF 행의 밑줄이 대신한다
	'.mnFormats .mnLangs': {
		borderTop: 0,
		paddingLeft: 20,
	},

	'.mnAction': {
		fontFamily: 'inherit',
		fontSize: 13,
		fontWeight: 500,
		letterSpacing: '.02em',
		color: mono.dim,
		background: 'transparent',
		border: 0,
		padding: 0,
		paddingBottom: 2,
		borderBottom: '1px solid transparent',
		cursor: 'pointer',
		transition: 'color 200ms ease-out, border-color 200ms ease-out',

		'&:hover': {
			color: mono.ink,
			borderBottomColor: mono.ink,
		},
		'&:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: 3,
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
})
